import type { ServiceAuftrag } from '../types'

function formatDate(str?: string) {
  if (!str) return '–'
  try {
    return new Date(str).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
  } catch { return str }
}

function truncate(str: string | undefined, n: number) {
  if (!str) return '–'
  return str.length > n ? str.slice(0, n) + '…' : str
}

export function printServiceLabel(auftrag: ServiceAuftrag) {
  const win = window.open('', '_blank', 'width=450,height=650')
  if (!win) {
    alert('Popup wurde blockiert. Bitte Popup-Blocker deaktivieren.')
    return
  }

  const qrUrl = `${window.location.origin}/service/${auftrag.id}`
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=90x90&data=${encodeURIComponent(qrUrl)}&qzone=1`

  const garantieBadge = auftrag.garantie
    ? `<span class="badge badge-garantie">GARANTIE</span>`
    : `<span class="badge badge-bezahlt">BEZAHLT</span>`

  const statusLabels: Record<string, string> = {
    offen: 'Offen',
    in_bearbeitung: 'In Bearbeitung',
    abgeschlossen: 'Abgeschlossen',
    versendet: 'Versendet',
  }

  const html = `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <title>${auftrag.nummer ?? 'Service-Auftrag'}</title>
  <style>
    @page {
      size: A6 portrait;
      margin: 0;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif;
      width: 105mm;
      min-height: 148mm;
      padding: 5mm;
      font-size: 9pt;
      color: #111;
      background: white;
    }

    /* Header */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 3mm;
      border-bottom: 0.5mm solid #000;
      margin-bottom: 3mm;
    }
    .header-left h1 {
      font-size: 13pt;
      font-weight: 800;
      letter-spacing: -0.3px;
      color: #000;
    }
    .header-left p {
      font-size: 7pt;
      color: #555;
      margin-top: 0.5mm;
    }
    .header-right {
      text-align: right;
    }
    .auftrag-nr {
      font-size: 11pt;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      color: #000;
    }
    .datum {
      font-size: 7pt;
      color: #555;
      margin-top: 0.5mm;
    }

    /* Sections */
    .section {
      margin-bottom: 2.5mm;
      padding-bottom: 2.5mm;
      border-bottom: 0.3mm solid #ddd;
    }
    .section:last-of-type { border-bottom: none; }
    .label {
      font-size: 6pt;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #888;
      margin-bottom: 0.8mm;
    }
    .value {
      font-size: 9.5pt;
      font-weight: 600;
      color: #000;
      line-height: 1.3;
    }
    .value-small {
      font-size: 8.5pt;
      font-weight: 400;
      color: #222;
      line-height: 1.4;
    }
    .mono {
      font-family: 'Courier New', monospace;
      font-size: 8.5pt;
    }

    /* Two-column row */
    .row {
      display: flex;
      gap: 4mm;
    }
    .row > div { flex: 1; }

    /* Badges */
    .badge {
      display: inline-block;
      padding: 1mm 3mm;
      border-radius: 2mm;
      font-size: 8pt;
      font-weight: 700;
      letter-spacing: 0.3px;
    }
    .badge-garantie { background: #fef3c7; color: #92400e; border: 0.3mm solid #f59e0b; }
    .badge-bezahlt  { background: #eff6ff; color: #1e40af; border: 0.3mm solid #3b82f6; }
    .badge-status   { background: #f0fdf4; color: #166534; border: 0.3mm solid #22c55e; }

    /* Footer */
    .footer {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      margin-top: 3mm;
      padding-top: 3mm;
      border-top: 0.5mm solid #000;
    }
    .footer-left { font-size: 7pt; color: #555; line-height: 1.5; }
    .footer-left strong { color: #000; font-size: 7.5pt; }
    .qr-img { display: block; }

    /* No-print button */
    .print-btn {
      display: block;
      margin: 8mm auto 0;
      padding: 3mm 10mm;
      background: #2563eb;
      color: white;
      border: none;
      border-radius: 3mm;
      font-size: 10pt;
      font-weight: 600;
      cursor: pointer;
    }
    @media print { .print-btn { display: none; } }
  </style>
</head>
<body>

  <!-- Header -->
  <div class="header">
    <div class="header-left">
      <h1>VAPOREX</h1>
      <p>Service &amp; Reparatur</p>
    </div>
    <div class="header-right">
      <div class="auftrag-nr">${auftrag.nummer ?? 'SERVICE'}</div>
      <div class="datum">${formatDate(auftrag.erstellt_am)}</div>
    </div>
  </div>

  <!-- Kunde -->
  <div class="section">
    <div class="label">Kunde</div>
    <div class="value">${auftrag.kunde_firma ?? auftrag.kunde_name ?? '–'}</div>
    ${auftrag.kunde_firma && auftrag.kunde_name !== auftrag.kunde_firma
      ? `<div class="value-small">${auftrag.kunde_name}</div>`
      : ''}
  </div>

  <!-- Gerät -->
  <div class="section">
    <div class="row">
      <div>
        <div class="label">Gerät</div>
        <div class="value">${auftrag.geraet_modell ?? '–'}</div>
      </div>
      <div>
        <div class="label">Seriennummer</div>
        <div class="value mono">${auftrag.seriennummer ?? '–'}</div>
      </div>
    </div>
  </div>

  <!-- Problem -->
  <div class="section">
    <div class="label">Fehlerbeschreibung</div>
    <div class="value-small">${truncate(auftrag.beschreibung, 120)}</div>
  </div>

  <!-- Abrechnung & Status -->
  <div class="section">
    <div class="row">
      <div>
        <div class="label">Abrechnung</div>
        <div style="margin-top:1mm">${garantieBadge}</div>
      </div>
      <div>
        <div class="label">Status</div>
        <div style="margin-top:1mm">
          <span class="badge badge-status">${statusLabels[auftrag.status] ?? auftrag.status}</span>
        </div>
      </div>
    </div>
  </div>

  ${auftrag.kosten > 0 ? `
  <!-- Kosten -->
  <div class="section">
    <div class="row">
      <div>
        <div class="label">Kosten</div>
        <div class="value">CHF ${auftrag.kosten.toFixed(2)}</div>
      </div>
      <div>
        <div class="label">Verrechnung</div>
        <div class="value">${auftrag.verrechnet ? '✓ Verrechnet' : 'Ausstehend'}</div>
      </div>
    </div>
  </div>` : ''}

  <!-- Footer mit QR -->
  <div class="footer">
    <div class="footer-left">
      <strong>Vaporex AG</strong><br>
      vaporex.ch<br>
      <br>
      ${auftrag.reparatur_log ? `<em>Notizen vorhanden</em>` : ''}
    </div>
    <div>
      <img class="qr-img" src="${qrSrc}" width="75" height="75" alt="QR" />
    </div>
  </div>

  <button class="print-btn" onclick="window.print()">🖨️ Drucken / Als PDF speichern</button>

  <script>
    // Auto-print after QR image loads
    const img = document.querySelector('img');
    if (img) {
      img.onload = () => setTimeout(() => window.print(), 300);
      img.onerror = () => setTimeout(() => window.print(), 300);
    } else {
      setTimeout(() => window.print(), 500);
    }
  </script>
</body>
</html>`

  win.document.write(html)
  win.document.close()
  win.focus()
}
